import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { PageHeader, Card, Table, Td, Badge, Empty, Btn } from "../../components/UI";
import { SlidePanel } from "../../components/SlidePanel";
import { FLabel } from "../engagement/FormControls";
import { useAuth } from "../../context/AuthContext";
import { Unlock, ShieldAlert, ChevronRight, Timer, XCircle, CheckCircle2 } from "lucide-react";

const KIND_LABELS = {
  progress_note: "Progress Note",
  assessment: "Assessment",
  treatment_plan: "Treatment Plan",
  crisis_plan: "Crisis or Safety Plan",
  consent_form: "Consent Form",
  chart_note: "Medical Note",
};

export default function UnlockRequestsAdmin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [approveTarget, setApproveTarget] = useState(null);
  const [denyTarget, setDenyTarget] = useState(null);
  const [windowHours, setWindowHours] = useState(2);
  const [adminNote, setAdminNote] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/unlock-requests", { params: { status: statusFilter } })
      .then((r) => { setRows(r.data); setLoading(false); })
      .catch((e) => { toast.error(apiError(e)); setLoading(false); });
  };
  useEffect(load, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const doApprove = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/unlock-requests/${approveTarget.id}/approve`,
        { window_hours: windowHours, admin_note: adminNote });
      toast.success(`Unlocked for ${windowHours}h. Requester notified.`);
      setApproveTarget(null); setAdminNote(""); setWindowHours(2);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const doDeny = async (e) => {
    e.preventDefault();
    if (adminNote.trim().length < 5) return toast.error("Denial note must be at least 5 characters");
    try {
      await api.post(`/unlock-requests/${denyTarget.id}/deny`, { admin_note: adminNote });
      toast.success("Request denied. Requester notified.");
      setDenyTarget(null); setAdminNote("");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const openRecord = (r) => {
    // Navigate based on resource_type. KISS: for progress_note and crisis_plan we have dedicated pages.
    if (r.resource_type === "progress_note") return navigate(`/clinical/progress-notes/${r.resource_id}`);
    if (r.resource_type === "crisis_plan") return navigate(`/clinical/crisis-plans/${r.resource_id}`);
    if (r.resource_type === "treatment_plan") return navigate(`/clinical/treatment-plans/${r.resource_id}`);
    if (r.resource_type === "assessment") return navigate(`/clinical/assessments/${r.resource_id}`);
    if (r.resource_type === "consent_form") return navigate(`/clinical/consents`);
    navigate(`/clients/${r.client_id}`);
  };

  return (
    <div data-testid="unlock-requests-page">
      <PageHeader title="Unlock Requests"
        subtitle={isAdmin
          ? "Compliance queue. Approve or deny requests to amend signed clinical records."
          : "Your submitted unlock requests. Admin decisions and time windows appear here."}
        action={<div className="flex gap-2">
          {["pending", "approved", "denied", "all"].map((s) => (
            <button key={s} data-testid={`unlock-req-filter-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`rounded-full px-3 py-1 text-xs font-bold ${statusFilter === s ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#5D4037] hover:bg-[#EDE7DC]"}`}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>} />
      <Card>
        {loading ? (
          <div className="p-6 text-sm text-[#7B6157]">Loading requests…</div>
        ) : rows.length === 0 ? (
          <Empty text={`No ${statusFilter === "all" ? "" : statusFilter + " "}unlock requests.`} />
        ) : (
          <Table headers={["Record", "Client", "Requester", "Reason", "Status", "Window", "Timeline", ""]}>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`unlock-req-row-${r.id}`} className="hover:bg-[#FDFCF8]">
                <Td className="font-medium text-[#3E2723]">
                  <div className="flex items-center gap-1.5">
                    <ShieldAlert size={13} className="text-[#8D5A00]" />
                    {KIND_LABELS[r.resource_type] || r.resource_type}
                  </div>
                </Td>
                <Td>{r.client_name || "-"}</Td>
                <Td>
                  <div className="text-xs text-[#5D4037]"><b>{r.requester_name}</b></div>
                  <div className="text-[10px] text-[#7B6157]">{r.requester_role}</div>
                </Td>
                <Td className="max-w-md">
                  <div className="text-xs text-[#3E2723] whitespace-pre-wrap">{r.reason}</div>
                </Td>
                <Td>
                  {r.status === "pending" && <Badge value="pending" />}
                  {r.status === "approved" && <span className="inline-flex items-center gap-1 rounded-full bg-[#E8F5E9] px-2 py-0.5 text-[10px] font-bold text-[#2E7D32]"><CheckCircle2 size={10} /> approved</span>}
                  {r.status === "denied" && <span className="inline-flex items-center gap-1 rounded-full bg-[#FFEBEE] px-2 py-0.5 text-[10px] font-bold text-[#C62828]"><XCircle size={10} /> denied</span>}
                </Td>
                <Td>
                  {r.status === "approved" && r.unlocked_until && (
                    <div className="flex items-center gap-1 text-[10px] text-[#8D5A00]">
                      <Timer size={11} /> {r.window_hours || "?"}h until {dayjs(r.unlocked_until).format("MMM D, h:mm A")}
                    </div>
                  )}
                </Td>
                <Td>
                  <div className="text-[10px] text-[#7B6157]">Requested {dayjs(r.created_at).format("MMM D, h:mm A")}</div>
                  {r.approved_at && <div className="text-[10px] text-[#2E7D32]">Approved by {r.approved_by_name} {dayjs(r.approved_at).format("MMM D, h:mm A")}</div>}
                  {r.denied_at && <div className="text-[10px] text-[#C62828]">Denied by {r.denied_by_name} {dayjs(r.denied_at).format("MMM D, h:mm A")}</div>}
                  {r.admin_note && <div className="text-[10px] italic text-[#5D4037]">&ldquo;{r.admin_note}&rdquo;</div>}
                </Td>
                <Td>
                  <div className="flex justify-end gap-1">
                    <button type="button" data-testid={`unlock-req-open-${r.id}`} onClick={() => openRecord(r)}
                      className="rounded-full bg-[#F5F2EB] p-1.5 text-[#5D4037] hover:bg-[#EDE7DC]" title="Open record">
                      <ChevronRight size={14} />
                    </button>
                    {isAdmin && r.status === "pending" && (
                      <>
                        <Btn data-testid={`unlock-req-approve-${r.id}`}
                          onClick={() => { setApproveTarget(r); setWindowHours(2); setAdminNote(""); }}
                          className="border-[#2E7D32] bg-[#2E7D32] text-white hover:bg-[#1B5E20]">
                          <Unlock size={11} /> Approve
                        </Btn>
                        <Btn variant="outline" data-testid={`unlock-req-deny-${r.id}`}
                          onClick={() => { setDenyTarget(r); setAdminNote(""); }}
                          className="border-[#C62828] text-[#C62828]">
                          <XCircle size={11} /> Deny
                        </Btn>
                      </>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <SlidePanel open={!!approveTarget} onClose={() => setApproveTarget(null)}
        title="Approve Unlock Request"
        subtitle="Grant a time window during which the record can be amended. After the window, it auto re-locks."
        testId="unlock-approve-panel">
        {approveTarget && (
          <form onSubmit={doApprove} className="space-y-3">
            <div className="rounded-xl border border-[#EDE7DC] bg-[#FDFCF8] p-3 text-xs text-[#5D4037]">
              <div><b>Requester:</b> {approveTarget.requester_name}</div>
              <div><b>Record:</b> {KIND_LABELS[approveTarget.resource_type]} for {approveTarget.client_name}</div>
              <div className="mt-2"><b>Reason:</b> {approveTarget.reason}</div>
            </div>
            <div>
              <FLabel>Amendment window (hours)</FLabel>
              <select data-testid="approve-window-hours" value={windowHours}
                onChange={(e) => setWindowHours(parseInt(e.target.value, 10))}
                className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]">
                <option value={1}>1 hour</option>
                <option value={2}>2 hours (recommended)</option>
                <option value={4}>4 hours</option>
                <option value={8}>8 hours</option>
                <option value={24}>24 hours (max)</option>
              </select>
            </div>
            <div>
              <FLabel>Admin note (optional)</FLabel>
              <textarea rows={2} value={adminNote} onChange={(e) => setAdminNote(e.target.value)}
                data-testid="approve-admin-note"
                placeholder="Any conditions or reminders for the requester."
                className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
            </div>
            <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-3">
              <Btn type="button" variant="ghost" onClick={() => setApproveTarget(null)}>Cancel</Btn>
              <Btn data-testid="approve-submit" type="submit" className="border-[#2E7D32] bg-[#2E7D32] text-white hover:bg-[#1B5E20]">
                <Unlock size={11} /> Grant {windowHours}h Window
              </Btn>
            </div>
          </form>
        )}
      </SlidePanel>

      <SlidePanel open={!!denyTarget} onClose={() => setDenyTarget(null)}
        title="Deny Unlock Request"
        subtitle="Requester will be notified. Denial reason is time-logged for compliance."
        testId="unlock-deny-panel">
        {denyTarget && (
          <form onSubmit={doDeny} className="space-y-3">
            <div className="rounded-xl border border-[#EDE7DC] bg-[#FDFCF8] p-3 text-xs text-[#5D4037]">
              <div><b>Requester:</b> {denyTarget.requester_name}</div>
              <div className="mt-2"><b>Their reason:</b> {denyTarget.reason}</div>
            </div>
            <div>
              <FLabel>Denial reason (minimum 5 characters)</FLabel>
              <textarea rows={3} required minLength={5} value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                data-testid="deny-admin-note"
                className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
            </div>
            <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-3">
              <Btn type="button" variant="ghost" onClick={() => setDenyTarget(null)}>Cancel</Btn>
              <Btn data-testid="deny-submit" type="submit" className="border-[#C62828] bg-[#C62828] text-white hover:bg-[#B71C1C]">
                <XCircle size={11} /> Deny Request
              </Btn>
            </div>
          </form>
        )}
      </SlidePanel>
    </div>
  );
}
